import { LEARNING_ORDER } from '../constants';

/**
 * Skill Progression Criteria
 * 
 * Defines requirements for advancing between skill levels.
 * Remember: This is GUIDANCE only - users can manually change levels anytime.
 */

export const PROGRESSION_CRITERIA = {
    // Level 1 → Level 2: Ready for Real-World Practice
    1: {
        name: "Ready for Real-World Practice",
        description: "You've mastered the basics and are ready for practical patterns",

        // PRIMARY PATH: Koch Method Completion
        primaryPath: {
            type: 'koch_completion',
            description: `Complete all ${LEARNING_ORDER.length} Koch Method lessons`,
            check: (userSettings, activities, progress) => {
                // Check if user has unlocked all characters
                const maxLevel = progress?.maxLevel || 0;
                return maxLevel >= LEARNING_ORDER.length;
            }
        },

        // ALTERNATIVE PATH: Performance-based
        alternativePath: {
            description: "Demonstrate proficiency through practice",
            requirements: [
                {
                    type: 'wpm_achievement',
                    threshold: 20,
                    duration: 60,
                    accuracy: 90,
                    sessions: 3,
                    description: "Achieve 20 WPM at 90%+ accuracy in 3 sessions (60s+ each)",
                    check: (userSettings, activities) => {
                        if (!activities || activities.length === 0) return { met: false, progress: 0 };

                        const qualifyingSessions = activities.filter(
                            act => act.wpm >= 20
                                && act.duration_seconds >= 60
                                && act.accuracy >= 90
                        );

                        return {
                            met: qualifyingSessions.length >= 3,
                            progress: Math.min(100, (qualifyingSessions.length / 3) * 100),
                            count: qualifyingSessions.length
                        };
                    }
                },
                {
                    type: 'character_mastery',
                    unique_characters: 25,
                    description: "Successfully copy at least 25 unique characters",
                    check: (userSettings, activities) => {
                        if (!activities || activities.length === 0) return { met: false, progress: 0 };

                        const uniqueChars = new Set();
                        activities.forEach(act => {
                            if (act.character_stats) {
                                Object.keys(act.character_stats).forEach(char => {
                                    if (act.character_stats[char].correct > 0) {
                                        uniqueChars.add(char);
                                    }
                                });
                            }
                        });

                        return {
                            met: uniqueChars.size >= 25,
                            progress: Math.min(100, (uniqueChars.size / 25) * 100),
                            count: uniqueChars.size
                        };
                    }
                }
            ]
        }
    },

    // Level 2 → Level 3: Ready for Expert Operations
    2: {
        name: "Ready for Expert Operations",
        description: "You're ready for real-world QSOs and contests",

        requirements: [
            {
                type: 'wpm_achievement',
                threshold: 25,
                duration: 120,
                accuracy: 85,
                sessions: 5,
                description: "Achieve 25 WPM at 85%+ accuracy in 5 sessions (120s+ each)",
                check: (userSettings, activities) => {
                    if (!activities || activities.length === 0) return { met: false, progress: 0 };

                    const qualifyingSessions = activities.filter(
                        act => act.wpm >= 25
                            && act.duration_seconds >= 120
                            && act.accuracy >= 85
                    );

                    return {
                        met: qualifyingSessions.length >= 5,
                        progress: Math.min(100, (qualifyingSessions.length / 5) * 100),
                        count: qualifyingSessions.length
                    };
                }
            },
            {
                type: 'mode_diversity',
                modes: ['callsigns', 'qcodes', 'activities', 'code_quiz'],
                sessions_per_mode: 3,
                description: "Practice callsigns, Q-codes, activities, and the Code Quiz (3 sessions each)",
                check: (userSettings, activities) => {
                    if (!activities || activities.length === 0) return { met: false, progress: 0 };

                    const modeCounts = {
                        callsigns: 0,
                        qcodes: 0,
                        activities: 0,
                        code_quiz: 0
                    };

                    activities.forEach(act => {
                        if (modeCounts.hasOwnProperty(act.mode)) {
                            modeCounts[act.mode]++;
                        }
                    });

                    const allMet = Object.values(modeCounts).every(count => count >= 3);
                    const totalProgress = Object.values(modeCounts).reduce((sum, count) => sum + Math.min(count, 3), 0);

                    return {
                        met: allMet,
                        progress: (totalProgress / 12) * 100,
                        modeCounts
                    };
                }
            },
            {
                type: 'streak',
                threshold: 5,
                description: "Maintain a 5-day practice streak",
                check: (userSettings) => {
                    const currentStreak = userSettings?.streak || 0;
                    return {
                        met: currentStreak >= 5,
                        progress: Math.min(100, (currentStreak / 5) * 100),
                        count: currentStreak
                    };
                }
            }
        ]
    },

    // Level 3 → Level 4: Ready for Head Copy
    3: {
        name: "Ready for Head Copy",
        description: "You're ready to master head copy and instant recognition",

        requirements: [
            {
                type: 'wpm_achievement',
                threshold: 30,
                duration: 180,
                accuracy: 90,
                sessions: 5,
                description: "Achieve 30 WPM at 90%+ accuracy in 5 sessions (180s+ each)",
                check: (userSettings, activities) => {
                    if (!activities || activities.length === 0) return { met: false, progress: 0 };

                    const qualifyingSessions = activities.filter(
                        act => act.wpm >= 30
                            && act.duration_seconds >= 180
                            && act.accuracy >= 90
                    );

                    return {
                        met: qualifyingSessions.length >= 5,
                        progress: Math.min(100, (qualifyingSessions.length / 5) * 100),
                        count: qualifyingSessions.length
                    };
                }
            },
            {
                type: 'qso_completion',
                count: 10,
                description: "Complete 10 simulated QSOs",
                check: (userSettings, activities) => {
                    if (!activities || activities.length === 0) return { met: false, progress: 0 };

                    const qsoSessions = activities.filter(act => act.mode === 'qso').length;

                    return {
                        met: qsoSessions >= 10,
                        progress: Math.min(100, (qsoSessions / 10) * 100),
                        count: qsoSessions
                    };
                }
            },
            {
                type: 'advanced_modes',
                description: "Experience contest or pileup operations",
                check: (userSettings, activities) => {
                    if (!activities || activities.length === 0) return { met: false, progress: 0 };

                    const advancedSessions = activities.filter(
                        act => act.mode === 'contests' || act.mode === 'pileup'
                    ).length;

                    return {
                        met: advancedSessions >= 5,
                        progress: Math.min(100, (advancedSessions / 5) * 100),
                        count: advancedSessions
                    };
                }
            }
        ]
    }
};

/**
 * Get level name for display
 */
export const getLevelName = (level) => {
    const names = {
        1: "Learning",
        2: "Improving",
        3: "Mastery",
        4: "Head Copy"
    };
    return names[level] || "Unknown";
};

/**
 * Get level description
 */
export const getLevelDescription = (level) => {
    const descriptions = {
        1: "The Basics",
        2: "Real World",
        3: "Expert Ops",
        4: "Head Copy"
    };
    return descriptions[level] || "";
};
